import React, { useState, useRef } from "react"
import { Flex, Box, Text, Button } from "rebass"
import { Label, Input, Select, Checkbox } from "@rebass/forms"

import theme from "../../../theme"
import ButtonsNav from "./ButtonsNav"
import RowWrapper from "./RowWrapper"

const FormField = ({ field }) => {
  const { label, formName, type, selectOptions, required } = field
  const [focused, setFocus] = useState(false)
  const [error, setError] = useState(false)
  const inputRef = useRef()

  const onFocus = () => {
    setFocus(true)
    setError(false)
  }
  const onBlur = () => {
    const value = inputRef.current.value.trim()
    setFocus(value ? true : false)
  }
  const onError = () => {
    setError(true)
  }

  const ID = `form-id-${formName}`;

  return (
    <Box
      as="aside"
      sx={{
        position: "relative",
      }}
    >
      {type === "select" ? (
        <Select
          id={ID}
          ref={inputRef}
          name={formName}
          required={required}
          onFocus={onFocus}
          onBlur={onBlur}
          onInvalid={onError}
          sx={{
            borderColor: error ? "red" : null,
            "& + svg": {
              color: error ? "red" : theme.colors.primary,
            },
          }}
        >
          <option value=""></option>
          {selectOptions.split(",").map((option, index) => {
            const value = option.trim()
            return (
              <option key={index} value={value}>
                {value}
              </option>
            )
          })}
        </Select>
      ) : (
        <Input
          id={ID}
          ref={inputRef}
          type={type}
          name={formName}
          required={required}
          onFocus={onFocus}
          onBlur={onBlur}
          onInvalid={onError}
          sx={{
            borderColor: error ? "red" : null,
          }}
        />
      )}

      <Label
        for={ID}
        sx={{
          position: "absolute",
          top: "50%",
          left: 3,
          display: "flex",
          alignItems: "center",
          width: "auto",
          height: "24px",
          backgroundColor: "background",
          color: focused ? "text" : "#777777",
          paddingLeft: 2,
          paddingRight: 2,
          pointerEvents: "none",
          transformOrigin: "top left",
          transform: `translate3d(0px, ${focused ? "-41px" : "-12px"}, 0)`,
          transition:
            "transform 250ms cubic-bezier(0.190, 1.000, 0.220, 1.000)" /* easeOutExpo */,
        }}
      >
        {label}
        {/*
        {required && (
          <Box as="span" color={error ? "red" : "primary"} pl={2}>
            *
          </Box>
        )}
        */}
      </Label>
    </Box>
  )
}

const FormCheckbox = ({ field }) => {
  const { label, required } = field
  return (
    <Box as="aside" sx={{ gridColumnEnd: ["span 1", null, "span 2"] }}>
      <Label
        sx={{
          display: "flex",
          alignItems: ["flex-start", null, "center"],
          fontSize: 0,
        }}
      >
        <Checkbox required={required} mr={[5, null, null, null, 2]} />
        {label}
      </Label>
    </Box>
  )
}

const RowFormComponent = ({ data, rowID }) => {
  const {
    title,
    titleHeadingTag,
    text,
    alignItems,
    justifyContent,
    buttonsButtons,
    formFieldsFormTitle,
    formFieldsSubmitLabel,
    formFieldsWebhookUrl,
    formFieldsFields,
  } = data

  const [submitted, setSubmitted] = useState(false)
  const [submitError, setSubmitError] = useState(false)

  const postData = async (url = "", formData) => {
    const response = await fetch(url, {
      method: "POST",
      mode: "cors",
      cache: "no-cache",
      redirect: "follow",
      referrerPolicy: "no-referrer",
      body: formData,
    })
    return await response.json()
  }

  const onSubmit = event => {
    event.preventDefault()
    const { currentTarget } = event
    const url = currentTarget.action
    const formData = new FormData(currentTarget)
    const submit = postData(url, formData)
    submit.then(data => {
      const { status } = data
      if (status === "success") {
        // set submitted
        setSubmitted(true)
      } else {
        setSubmitError(true)
      }
    })
  }

  return (
    <RowWrapper {...data}>
      <Flex
        flexDirection={["column", null, null, "row"]}
        justifyContent={justifyContent}
        alignItems={alignItems}
      >
        <Box
          as="aside"
          width={[1, null, null, 1 / 2]}
          pl={["15px", null, 4, 0]}
          pr={["15px", null, 4]}
          pb={[5, null, null, 0]}
        >
          <Box maxWidth="500px" mx="auto">
            {title && (
              <Text as={titleHeadingTag} mb={3}>
                {title}
              </Text>
            )}
            {text && (
              <Box
                dangerouslySetInnerHTML={{ __html: text }}
                textAlign={justifyContent}
              />
            )}
            {buttonsButtons && <ButtonsNav data={buttonsButtons} mt={3} />}
          </Box>
        </Box>

        <Box as="aside" width={[1, null, null, 1 / 2]}>
          {formFieldsFormTitle && (
            <Text
              as="small"
              sx={{
                display: "block",
                fontFamily: "heading",
                fontSize: 0,
                lineHeight: 1.214285714,
                color: "#4A494A",
                paddingLeft: ["15px", null, 4],
                paddingRight: ["15px", null, 4],
                marginBottom: 3,
              }}
            >
              {formFieldsFormTitle}
            </Text>
          )}

          <Box
            action={formFieldsWebhookUrl}
            as="form"
            onSubmit={e => onSubmit(e)}
            bg="background"
            py={["50px", null, 4]}
            px={["30px", null, 4]}
            sx={{
              display: "grid",
              gridTemplateColumns: submitted
                ? ["1fr"]
                : ["1fr", null, "1fr 1fr"],
              gridAutoRows: "auto",
              gridColumnGap: "26px",
              gridRowGap: "25px",
              borderWidth: "1px",
              borderStyle: "solid",
              borderColor: "#E0E0E0",
              borderRadius: "small",
            }}
          >
            {/* Submit form data error  */}
            {submitError && (
              <Text as="h3" color="red">
                Erreur de communication
              </Text>
            )}

            {/* Before submitted form */}
            {!submitted && (
              <React.Fragment>
                {formFieldsFields.map((field, index) => {
                  const FieldType =
                    field.type === "checkbox" ? FormCheckbox : FormField
                  return <FieldType key={index} field={field} />
                })}

                <Button
                  variant="cta-filled"
                  mt={1}
                  sx={{
                    color: "#F6F6F6",
                    cursor: "pointer",
                    gridColumnEnd: ["span 1", null, "span 2"],
                  }}
                >
                  {formFieldsSubmitLabel}
                </Button>
              </React.Fragment>
            )}
            {/* When form is submitted */}
            {submitted && (
              <Text as="h5" width={[`100%`]}>
                Merci! Un membre de l'équipe ESMA vous contactera sous peu.
              </Text>
            )}
          </Box>
        </Box>
      </Flex>
    </RowWrapper>
  )
}

export default RowFormComponent
